import { ExclamationCircleIcon } from '@heroicons/react/20/solid';
import { CurrencyRupeeIcon } from '@heroicons/react/24/outline';
import classNames from 'classnames';
import { type ChangeEvent, cloneElement, useEffect, useState } from 'react';

import { formatNumber } from 'utils/format-helper';

function formatValue(
  type: 'email' | 'password' | 'number' | 'text' | 'currency' | 'image_url',
  value?: string | number
) {
  if (!value) return '-';

  if (type === 'currency') {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    }).format(Number(value));
  }

  if (type === 'password') return '•'.repeat(String(value).length);

  if (type === 'number') return new Intl.NumberFormat('en-IN').format(Number(value));

  if (type === 'image_url') {
    const url = String(value);
    return <img src={url} alt={url} height={50} />;
  }

  return typeof value === 'number' ? String(value) : value;
}

/** Keeps digits and at most one "." for decimal typing. */
function sanitizeDecimalTyping(raw: string): string {
  const only = raw.replace(/[^\d.]/g, '');
  const dot = only.indexOf('.');
  if (dot === -1) return only;
  return `${only.slice(0, dot + 1)}${only.slice(dot + 1).replace(/\./g, '')}`;
}

function numericStringToValue(s: string): number | '' {
  const trimmed = s.trim();
  if (trimmed === '') return '';
  const n = Number(trimmed);
  return Number.isFinite(n) ? n : '';
}

function parseNumericProp(value?: string | number | null): number | null {
  if (value === undefined || value === null || value === '') return null;
  const n = typeof value === 'number' ? value : Number(value);
  return Number.isFinite(n) ? n : null;
}

/** Emits normalized numeric string (no commas) for FormPanel `Number(...)`. */
function emitNumericChange(
  onChange: (e: ChangeEvent<HTMLInputElement>) => void,
  emitted: number | ''
) {
  const str = emitted === '' ? '' : String(emitted);
  onChange({
    target: { value: str },
    currentTarget: { value: str },
  } as ChangeEvent<HTMLInputElement>);
}

export default function TextField({
  onChange,
  value,
  error,
  label,
  placeholder,
  type = 'text',
  fullWidth = false,
  helperText,
  leadingIcon,
  readOnly,
  uppercase,
}: {
  onChange: (e: ChangeEvent<HTMLInputElement>) => void;
  value?: string | number;
  label?: string;
  error?: string;
  placeholder?: string;
  type?: 'email' | 'password' | 'number' | 'text' | 'currency' | 'image_url';
  fullWidth?: boolean;
  helperText?: string;
  leadingIcon?: any;
  readOnly?: boolean;
  uppercase?: boolean;
}) {
  const icon = type === 'currency' ? <CurrencyRupeeIcon /> : undefined;

  const [currencyFocused, setCurrencyFocused] = useState(false);
  const [currencyDraft, setCurrencyDraft] = useState('');

  const normalizedNumber = parseNumericProp(value ?? null);

  useEffect(() => {
    if (type !== 'currency' || readOnly) return;
    if (!currencyFocused) {
      setCurrencyDraft('');
    }
  }, [value, type, readOnly, currencyFocused]);

  function content() {
    if (readOnly) {
      return (
        <div className="block py-2.5 text-gray-900 sm:text-sm">{formatValue(type, value)}</div>
      );
    }

    if (type === 'currency') {
      const blurredFormatted = normalizedNumber === null ? '' : formatNumber(normalizedNumber);
      const visibleValue = currencyFocused ? currencyDraft : blurredFormatted;

      return (
        <div className="relative mt-1 rounded-md">
          <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
            {cloneElement(icon as React.ReactElement, {
              className: classNames('h-5 w-5', {
                'text-gray-400': !error,
                'text-red-500': error,
              }),
            })}
          </div>
          <input
            type="text"
            inputMode="decimal"
            autoComplete="off"
            spellCheck={false}
            aria-label={label ? undefined : 'Amount in INR'}
            name={label}
            id={label}
            className={classNames(
              'block rounded-md border-0 py-1.5 pl-10 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-gray-600 sm:text-sm sm:leading-6',
              {
                'w-full': fullWidth,
                'pr-10 ring-inset ring-red-300 focus:ring-red-600': error,
              }
            )}
            placeholder={placeholder}
            value={visibleValue}
            onFocus={() => {
              const plain = normalizedNumber === null ? '' : String(normalizedNumber);
              setCurrencyDraft(plain);
              setCurrencyFocused(true);
            }}
            onBlur={() => {
              setCurrencyFocused(false);
              setCurrencyDraft('');
            }}
            onChange={e => {
              const next = sanitizeDecimalTyping(e.target.value);
              setCurrencyDraft(next);
              emitNumericChange(onChange, numericStringToValue(next));
            }}
          />
          {error && (
            <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3">
              <ExclamationCircleIcon className="h-5 w-5 text-red-500" aria-hidden="true" />
            </div>
          )}
        </div>
      );
    }

    return (
      <div className="relative mt-1 rounded-md">
        <input
          type={type}
          name={label}
          id={label}
          className={classNames(
            'block rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-gray-600 sm:text-sm sm:leading-6',
            {
              'w-full': fullWidth,
              'pr-10 ring-inset ring-red-300 focus:ring-red-600': error,
              'pl-10': leadingIcon,
              uppercase: uppercase,
            }
          )}
          placeholder={placeholder}
          onChange={onChange}
          value={value}
        />
        {error && (
          <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3">
            <ExclamationCircleIcon className="h-5 w-5 text-red-500" aria-hidden="true" />
          </div>
        )}
      </div>
    );
  }

  return (
    <div>
      {label && (
        <label htmlFor={label} className="block text-sm font-medium leading-6 text-gray-900">
          {label}
        </label>
      )}
      {content()}
      {helperText && !error && <p className="mt-2 text-sm text-gray-500">{helperText}</p>}
      {error && (
        <p className="mt-2 text-sm text-red-600" id="error">
          {error}
        </p>
      )}
    </div>
  );
}
